'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

interface PublicNavProps {
  activePage?: 'about' | 'services' | 'how-it-works' | 'contact' | 'track' | 'shipment-tracking';
  scrolled?: boolean;
  /** Pass true when a user is authenticated (homepage uses this) */
  isAuthenticated?: boolean;
  /** Dashboard route to redirect to when authenticated */
  dashboardRoute?: string;
}

export default function PublicNav({ activePage, scrolled = true, isAuthenticated = false, dashboardRoute = '/customer-account-dashboard' }: PublicNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const router = useRouter();

  const navLinks = [
    { label: 'About', href: '/about', key: 'about' },
    { label: 'Services', href: '/services', key: 'services' },
    { label: 'How It Works', href: '/how-it-works', key: 'how-it-works' },
    { label: 'Track', href: '/track', key: 'track' },
    { label: 'Shipment Tracking', href: '/track-shipment', key: 'shipment-tracking' },
    { label: 'Contact', href: '/contact', key: 'contact' },
  ];

  const handleSignIn = () => {
    if (isAuthenticated) {
      router.push(dashboardRoute);
    } else {
      router.push('/sign-up-login-screen');
    }
  };

  return (
    <nav className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/95 backdrop-blur-xl border-b border-border/40' : 'bg-background/95 backdrop-blur-xl border-b border-border/40'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <Image
              src="/assets/images/ChatGPT_Image_Jul_15__2026__06_13_59_PM-1784139267730.png"
              alt="Kars Postasi Ltd crest logo"
              width={40}
              height={40}
              className="object-contain"
              style={{ mixBlendMode: scrolled ? 'multiply' : 'screen' }}
              priority
            />
          </div>
          <div>
            <p className="text-primary font-bold text-[15px] leading-tight">KARS POSTASI LTD</p>
            <p className="text-muted-foreground text-[9px] uppercase tracking-widest font-600">Security · Privacy · Trust</p>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map(link => (
            <Link
              key={link.key}
              href={link.href}
              className={`text-[13px] font-500 transition-colors ${activePage === link.key ? 'text-primary font-600' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={handleSignIn}
            className="text-[13px] font-600 border border-border bg-background/80 hover:text-primary hover:bg-muted transition-colors px-4 py-2 rounded-lg min-h-[44px] flex items-center text-[rgba(241,152,152,1)]"
          >
            {isAuthenticated ? 'Dashboard' : 'Sign In'}
          </button>
          <Link href="/sign-up-login-screen" className="text-[13px] font-600 text-white vault-gradient px-4 py-2 rounded-lg shadow-card hover:opacity-90 transition-all min-h-[44px] flex items-center">
            Open Account
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            {menuOpen ? (
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            ) : (
              <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-background border-b border-border/40 px-4 pb-5 pt-2 space-y-1">
          {navLinks.map(link => (
            <Link
              key={link.key}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className={`block py-3 text-[14px] font-500 border-b border-border/30 last:border-0 min-h-[48px] flex items-center ${activePage === link.key ? 'text-primary font-600' : 'text-foreground hover:text-primary'}`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-3 flex gap-3">
            <button
              onClick={() => { setMenuOpen(false); handleSignIn(); }}
              className="flex-1 text-center text-[13px] font-600 border border-border rounded-lg py-3 hover:bg-muted transition-colors min-h-[48px] flex items-center justify-center"
            >
              {isAuthenticated ? 'Dashboard' : 'Sign In'}
            </button>
            <Link href="/sign-up-login-screen" className="flex-1 text-center text-[13px] font-600 text-white vault-gradient rounded-lg py-3 min-h-[48px] flex items-center justify-center">
              Open Account
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
